import React, { useState } from "react";
import { toast } from "sonner";
import { ScanLine } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { providerShort } from "./psPrimitives";
import { ConfirmSheet } from "./ProvidersScreen";
import { tovSlug, tovSlots, providersOfClient, providerClientId } from "@/lib/tov";
import { FormatSpecView } from "./FormatSpecView";

const IND = "#5856D6";
const SCOPE = "Used for dictated and telemedicine notes · uploaded and pasted notes are only tagged.";

// "Default — every visit" · client tov_list · orphaned slots
export function TovSelect({ names, slots, value, onChange, onSetup }) {
  const inList = new Set(names.map(tovSlug));
  const orphans = Object.keys(slots).filter((k) => !inList.has(k));
  if (!names.length && !orphans.length) {
    return (
      <div className="flex items-center gap-2.5 mb-4 text-[11.5px] text-[var(--gray2)]" data-testid="note-tov-none">
        <span className="font-semibold">Visit type</span>
        <span>No visit types for this client yet ·</span>
        <button onClick={onSetup} data-testid="note-tov-setup" className="font-semibold" style={{ color: IND }}>Set them up ›</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <label htmlFor="note-tov-select" className="text-[11px] font-semibold text-[var(--gray2)]">Visit type</label>
      <select id="note-tov-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid="note-tov-select"
        className="rounded-[9px] border border-black/[.14] bg-white px-2.5 py-1.5 text-[12.5px] text-[var(--ink)] focus:border-[#5856D6] outline-none">
        <option value="">Default — every visit</option>
        {names.map((n) => { const k = tovSlug(n); return <option key={k} value={k}>{n}{slots[k] ? " · own template" : ""}</option>; })}
        {orphans.length ? (
          <optgroup label="No longer in the visit type list">
            {orphans.map((k) => <option key={k} value={k}>{slots[k]?.label || k}</option>)}
          </optgroup>
        ) : null}
      </select>
    </div>
  );
}

function Outline({ fs, dim }) {
  return <FormatSpecView fs={fs} dim={dim} />;
}

export function TovStyle({ busy, provider, allProviders, tovKey, curName, slot, own, defaultFs, defaultIsCustom, short, onPick, onChanged }) {
  const npi = provider.npi_number;
  const [confirmDefault, setConfirmDefault] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState("");
  const others = providersOfClient(allProviders, providerClientId(provider)).filter((p) => p.npi_number !== npi);

  const useDefault = async () => {
    setWorking(true);
    try { await api.delete(`/api/edi/note-to-claim/providers/${npi}/soap-profile?tov=${encodeURIComponent(curName)}`); onChanged(); setConfirmDefault(false); }
    catch (e) { toast.error(describeError(e)); }
    setWorking(false);
  };

  // POST inherit — response shape unconfirmed; read `inherited` defensively
  const copyFrom = async (o) => {
    const oShort = providerShort(o.provider_name);
    setWorking(true); setResult("");
    try {
      const { data } = await api.post(`/api/edi/note-to-claim/providers/${npi}/soap-profile/inherit`, { from_npi: o.npi_number, tov: curName });
      const inh = Array.isArray(data?.inherited) ? data.inherited : null;
      if (inh && !inh.length) setResult(`${oShort} has no template or exam defaults to copy yet.`);
      else {
        const theirDefault = !tovSlots(o)[tovKey] || (inh || []).some((x) => /default/i.test(typeof x === "string" ? x : JSON.stringify(x)));
        setResult(`Copied ${oShort}'s template and exam defaults${theirDefault ? " (their Default)" : ""}`);
      }
      onChanged();
    } catch (e) {
      const st = e?.response?.status;
      setResult(st === 400 || st === 404 ? `${oShort} has no template or exam defaults to copy yet.` : describeError(e));
    }
    setWorking(false);
  };

  if (busy) return (
    <div className="rounded-[16px] border border-[#E9E9EC] bg-white p-8 text-center">
      <div className="text-[13px] font-semibold text-[var(--ink)]">Lens is reading the {curName} note…</div>
      <div className="mt-4 h-1.5 rounded-full overflow-hidden bg-black/[.06]"><div className="h-full w-1/3 rounded-full animate-pulse" style={{ background: IND }} /></div>
    </div>
  );

  const resultLine = result ? <div className="text-[11.5px] text-[var(--ink2)] mb-3" data-testid="note-tov-copy-result">{result}</div> : null;
  const sheet = confirmDefault ? (
    <ConfirmSheet title={`Use the Default template for ${curName}?`} busy={working} confirmLabel="Use Default"
      body={`${curName} notes go back to ${short}'s Default template. This visit type's own exam defaults${slot?.ros_pe?.exception_based ? " and its charting-by-exception attestation" : ""} are cleared too.`}
      onConfirm={useDefault} onCancel={() => setConfirmDefault(false)} />
  ) : null;

  if (own) {
    return (
      <div data-testid="note-tov-own">
        <div className="rounded-[12px] bg-[var(--ink)] text-white px-4 py-2.5 text-[11px] mb-1.5 flex items-center gap-3 flex-wrap">
          <ScanLine size={14} />
          <span className="flex-1 min-w-[200px]">Lens built {short}'s <strong>{curName}</strong> template{slot?.format_sample?.filename ? <> from <strong>{slot.format_sample.filename}</strong></> : null}.</span>
          <button onClick={onPick} data-testid="note-tov-replace" className="text-[10.5px] font-semibold underline">Replace sample…</button>
          <button onClick={() => setConfirmDefault(true)} data-testid="note-tov-use-default" className="text-[10.5px] font-semibold underline" style={{ color: "#FF8A80" }}>Use Default instead</button>
        </div>
        <div className="text-[10.5px] text-[var(--gray3)] mb-4">{SCOPE}</div>
        {resultLine}
        <Outline fs={slot?.format_spec} />
        {sheet}
      </div>
    );
  }

  return (
    <div data-testid="note-tov-inherit">
      <div className="rounded-[12px] bg-[rgba(88,86,214,.05)] px-4 py-3 mb-1.5 flex items-center gap-3 flex-wrap">
        <span className="flex-1 min-w-[220px] text-[11.5px] text-[var(--ink2)]" data-testid="note-tov-strip">
          <b className="text-[var(--ink)]">{curName}</b> notes use {short}'s {defaultIsCustom ? "Default template" : "built-in format"}.
        </span>
        <button onClick={onPick} disabled={working} data-testid="note-tov-upload" className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC] bg-white text-[var(--ink)]">Upload a sample…</button>
        {others.map((o) => (
          <button key={o.npi_number} onClick={() => copyFrom(o)} disabled={working} data-testid={`note-tov-copy-${o.npi_number}`}
            className="text-[12px] font-semibold" style={{ color: IND }}>Copy from {providerShort(o.provider_name)}</button>
        ))}
      </div>
      <div className="text-[10.5px] text-[var(--gray3)] mb-4">{SCOPE}</div>
      {resultLine}
      <div className="rounded-[16px] border-2 border-dashed border-black/[.14] bg-white/40 p-6 text-center mb-4" data-testid="note-tov-dropzone">
        <ScanLine size={20} className="mx-auto text-[var(--gray2)]" />
        <div className="text-[13px] font-semibold text-[var(--ink)] mt-2.5">Upload a {curName} note via Lens</div>
        <div className="text-[11.5px] text-[var(--gray2)] mt-1 max-w-[460px] mx-auto leading-relaxed">Lens learns the layout {short} uses for {curName}. Until then these notes use the layout below.</div>
        <button onClick={onPick} data-testid="note-tov-dropzone-upload" className="btn-ink inline-flex items-center gap-1.5 mt-3.5"><ScanLine size={14} /> Upload via Lens…</button>
      </div>
      <Outline fs={defaultIsCustom ? defaultFs : null} dim />
      {sheet}
    </div>
  );
}
